import { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { MinisterListTemplate } from '@/features/ministers/templates';
import {
  useUserProfile,
  useMinisterList,
  useMinister,
  useMinisterForm,
} from '@/features/ministers/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { getDegreesList, type MinisterLinkedRecords } from '@/data/ministerRepository';

type DeleteDialogState = 'idle' | 'checking' | 'blocked' | 'ready';
import { getRegionsByDistrict } from '@/Services/regions.service';
import { useToast } from '@/hooks/use-toast';
import type { District } from '@/models/district';
import type { Region } from '@/models/region';
import type { Degree } from '@/models/degree';
import type { Minister } from '@/models/minister';
import { InlineLoader } from '@/components/ui/pulsing-loader';

interface TerritoryContext {
  district: District | null;
  region: Region | null;
  isDistrict: boolean;
}

/**
 * Ministers Tab — scoped minister list within territory layout.
 * Passes district/region as scope filters; table handles the rest internally.
 */
export default function MinistersTab() {
  const { district, region, isDistrict } = useOutletContext<TerritoryContext>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { user, isLoading: isLoadingProfile } = useUserProfile();
  const { can, isLoading: isLoadingPermissions } = usePermissions();
  const isLoadingUser = isLoadingProfile || isLoadingPermissions;

  // Form dropdown data
  const [degrees, setDegrees] = useState<Degree[]>([]);
  const [districtRegions, setDistrictRegions] = useState<Region[]>([]);
  const [currentDistrict, setCurrentDistrict] = useState<District | null>(null);

  useEffect(() => {
    getDegreesList()
      .then(setDegrees)
      .catch((e) => console.error('Error loading degrees:', e));
  }, []);

  // Resolve current district from context
  useEffect(() => {
    if (isDistrict) {
      setCurrentDistrict(district);
    } else if (region?.district) {
      setCurrentDistrict(region.district as District);
    }
  }, [isDistrict, district, region]);

  // Load regions for the create/edit form
  useEffect(() => {
    const districtId = currentDistrict?.id;
    if (!districtId) { setDistrictRegions([]); return; }
    getRegionsByDistrict(districtId)
      .then(setDistrictRegions)
      .catch(() => setDistrictRegions([]));
  }, [currentDistrict?.id]);

  // Scope filters — territory restricts which ministers are visible
  const scopeFilters = useMemo(() => {
    if (isDistrict && district) return { districtId: district.id };
    if (!isDistrict && region) return { districtId: region.district_id, regionId: region.id };
    return undefined;
  }, [isDistrict, district, region]);

  const { ministers, isLoading, reload } = useMinisterList(scopeFilters);

  const { createMinister, updateMinister, getMinisterLinkedRecords, hardDeleteMinister, isSubmitting, isDeleting } =
    useMinister();

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingMinister, setEditingMinister] = useState<Minister | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingMinister, setViewingMinister] = useState<Minister | null>(null);
  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>('idle');
  const [deletingMinister, setDeletingMinister] = useState<Minister | null>(null);
  const [linkedRecords, setLinkedRecords] = useState<MinisterLinkedRecords | null>(null);

  const showDeleteDialog = deleteDialogState !== 'idle';

  const ministerForm = useMinisterForm(editingMinister || undefined);

  const handleCreateNew = () => {
    if (!currentDistrict) {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo cargar la información del distrito.' });
      return;
    }
    setEditingMinister(null);
    ministerForm.reset();
    setTimeout(() => {
      ministerForm.updateField('district_id', currentDistrict.id);
      if (!isDistrict && region) {
        setTimeout(() => ministerForm.updateField('region_id', region.id), 0);
      }
    }, 0);
    setShowFormDialog(true);
  };

  const handleView = (minister: Minister) => { setViewingMinister(minister); setShowViewDialog(true); };
  const handleEdit = (minister: Minister) => navigate(`/ministers/${minister.id}`);
  const handleDeleteClick = async (minister: Minister) => {
    setDeletingMinister(minister);
    setLinkedRecords(null);
    setDeleteDialogState('checking');
    const links = await getMinisterLinkedRecords(minister.id);
    if (!links) {
      setDeleteDialogState('idle');
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo verificar los registros vinculados.' });
      return;
    }
    setLinkedRecords(links);
    setDeleteDialogState(links.total > 0 ? 'blocked' : 'ready');
  };

  const handleFormSubmit = async () => {
    if (!user?.id) {
      toast({ variant: 'destructive', title: 'Usuario no autenticado', description: 'Debe iniciar sesión para realizar esta acción.' });
      return;
    }
    const isValid = await ministerForm.validate();
    if (!isValid) {
      toast({ variant: 'destructive', title: 'Errores en el formulario', description: 'Por favor corrija los errores marcados antes de continuar.' });
      return;
    }

    let result: { minister: Minister | null; photoError?: string } | null = null;

    if (editingMinister) {
      result = await updateMinister(editingMinister.id, ministerForm.formData, user.id, ministerForm.processedPhotoFile, ministerForm.photoFile, editingMinister, ministerForm.shouldDeletePhoto);
    } else {
      result = await createMinister(ministerForm.formData, user.id, ministerForm.processedPhotoFile, ministerForm.photoFile);
    }

    if (result?.minister) {
      toast({ title: editingMinister ? 'Ministro actualizado' : 'Ministro creado', description: `Ministro ${result.minister.adb_code} ${editingMinister ? 'actualizado' : 'creado'} exitosamente.` });
      if (result.photoError) {
        toast({ variant: 'destructive', title: 'Error al subir foto', description: result.photoError + ' - Los datos del ministro se guardaron correctamente.' });
      }
      setShowFormDialog(false);
      setEditingMinister(null);
      ministerForm.reset();
      reload();
    } else {
      toast({ variant: 'destructive', title: 'Error', description: `No se pudo ${editingMinister ? 'actualizar' : 'crear'} el ministro.` });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingMinister) return;
    const success = await hardDeleteMinister(deletingMinister.id);
    if (success) {
      toast({ title: 'Ministro eliminado', description: `Ministro ${deletingMinister.adb_code} eliminado permanentemente.` });
      setDeleteDialogState('idle');
      setDeletingMinister(null);
      setLinkedRecords(null);
      reload();
    } else {
      toast({ variant: 'destructive', title: 'Error al eliminar', description: 'No se pudo eliminar el ministro.' });
    }
  };

  const handleCloseDeleteDialog = () => {
    if (isDeleting) return;
    setDeleteDialogState('idle');
    setDeletingMinister(null);
    setLinkedRecords(null);
  };

  if (isLoadingUser) return <InlineLoader />;

  return (
    <div className="p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <MinisterListTemplate
          ministers={ministers}
          districts={currentDistrict ? [currentDistrict] : []}
          allRegions={isDistrict ? districtRegions : (region ? [region] : [])}
          degrees={degrees}
          isLoading={isLoading}
          canCreate={can('minister.create', { districtId: isDistrict ? district?.id : region?.district_id, regionId: isDistrict ? null : region?.id })}
          hideDistrictColumn={true}
          hideRegionColumn={!isDistrict}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onCreateNew={handleCreateNew}
          showViewDialog={showViewDialog}
          onCloseViewDialog={() => { setShowViewDialog(false); setViewingMinister(null); }}
          viewingMinister={viewingMinister}
          showFormDialog={showFormDialog}
          onCloseFormDialog={() => { setShowFormDialog(false); setEditingMinister(null); ministerForm.reset(); }}
          formData={ministerForm.formData}
          formErrors={ministerForm.errors}
          formTouched={ministerForm.touched}
          onFormFieldChange={ministerForm.updateField}
          onFormFieldBlur={ministerForm.markFieldTouched}
          onFormSubmit={handleFormSubmit}
          photoPreview={ministerForm.photoPreview}
          onPhotoSelect={ministerForm.handlePhotoSelect}
          shouldDeletePhoto={ministerForm.shouldDeletePhoto}
          isSubmitting={isSubmitting}
          editingMinister={editingMinister}
          showDeleteDialog={showDeleteDialog}
          deleteDialogState={deleteDialogState}
          linkedRecords={linkedRecords}
          onCloseDeleteDialog={handleCloseDeleteDialog}
          onConfirmDelete={handleDeleteConfirm}
          deletingMinister={deletingMinister}
          isDeleting={isDeleting}
        />
      </div>
    </div>
  );
}
